import { getDeviceTrialStatus, deviceTrialRemainingMs, clearDeviceTrialLease } from './deviceTrial';
import { updateTelegramTrialLease, telegramTrialRemainingMs } from './telegramTrial';
import type { WorkflowRequest } from '../../shared/companyWorkflowRequest';
import { calculateCompanyPlanPrice, COMPANY_PLAN_PRICING_VERSION, type CompanyPlanLane, type CompanyPlanPricingVersion } from '../../shared/companyPlanPricing';
import type { CompanyServiceSelections } from '../../shared/companyServiceSelections';
import { accountStorageKey } from './accountSessions';
import { legacyAuthHeader } from '../utils/legacyHostCompat';
/**
 * Subscription Service
 * Handles subscription status checking, promotions, and checkout flow
 */

import { hostWebApp } from './hostWebApp';
import { isIosNativeAppRuntime } from './runtimeCapabilities';
import { iosStoreKitService, planForIosProductId } from './iosStoreKit';
import { connectApplePurchase } from './applePurchaseSession';
import { setNativeIosLinkedUser } from './nativeApiRouting';
import { getWebSessionToken, WEB_SESSION_HEADER } from './webSessionAuth';
const LEGACY_AUTH_USER_ID_HEADER = legacyAuthHeader('User-Id');
const LEGACY_AUTH_USERNAME_HEADER = legacyAuthHeader('Username');
const LEGACY_AUTH_INIT_DATA_HEADER = legacyAuthHeader('Init-Data');


const API_BASE = '/api/subscription';
const SUBSCRIPTION_GRACE_STORAGE_KEY = 'ziawellnav_subscription_last_good';
const SUBSCRIPTION_GRACE_TTL_MS = 24 * 60 * 60 * 1000;

export interface SubscriptionStatus {
  isSubscribed: boolean;
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | 'error';
  type?: 'dayPass' | 'monthly' | 'yearly' | 'business_starter' | 'company_owner' | 'company_employee';
  rawPlan?: string;
  expiresAt?: string;
  isTrialing: boolean;
  trialDaysRemaining: number;
  canTrial: boolean;
  checkedAt?: string;
  lastError?: string;
  source?: 'cache' | 'api' | 'http' | 'network' | 'parse';
  httpStatus?: number;
  hint?: string;
  accessSource?: 'individual_subscription' | 'trial' | 'business_seat' | 'none' | string;
  paymentRequired?: boolean;
  companyPlanRequired?: boolean;
  businessSeat?: {
    companyId: string;
    purchasedSeats: number;
    billedSeats: number;
    adminSeats: number;
    source: string;
  } | null;
  isBusinessPlus?: boolean;
  businessPlusFeatures?: string[];
  companyName?: string;
  companyRole?: string;
  /** Server-scoped free access (e.g. waste_management rentals only). */
  scopedAccess?: string | null;
  /** Open dumpster rental that unlocked wm_rental_customer access. */
  wmRental?: {
    jobId?: string;
    status?: string;
    siteName?: string | null;
    dumpsterSize?: string | null;
  } | null;
}

/** Dumpster customers with an open rental — free WM only, not full oilfield suite. */
export function isWmRentalAccess(status: Pick<SubscriptionStatus, 'accessSource' | 'scopedAccess' | 'rawPlan'> | null | undefined): boolean {
  if (!status) return false;
  const source = String(status.accessSource || '');
  if (source === 'wm_rental_customer') return true;
  if (String(status.scopedAccess || '') === 'waste_management') return true;
  if (String(status.rawPlan || '') === 'wm_rental_customer') return true;
  return false;
}

export interface PromoValidation {
  valid: boolean;
  code?: string;
  description?: string;
  discountType?: 'percent' | 'fixed';
  discountValue?: number;
  discountDisplay?: string;
  appliesTo?: 'all' | 'monthly' | 'yearly';
  expiresAt?: string;
  error?: string;
}

// Plan ID types - includes both new and legacy IDs for backward compatibility
export type NewPlanId = 'individual_day_pass' | 'individual_monthly' | 'individual_yearly' | 'business_starter' | 'business_team' | 'business_fleet' | 'business_enterprise';
export type LegacyPlanId = 'monthly' | 'yearly' | 'business_plus';
export type PlanId = NewPlanId | LegacyPlanId;

// Map legacy IDs to new IDs
export const LEGACY_PLAN_MAP: Record<LegacyPlanId, NewPlanId> = {
  'monthly': 'individual_monthly',
  'yearly': 'individual_yearly', 
  'business_plus': 'business_starter'
};

export interface PricingPlan {
  id: PlanId;
  name: string;
  price: number;
  originalPrice?: number;
  interval: 'day' | 'month' | 'year';
  trialDays: number;
  features: string[];
  popular?: boolean;
  savings?: string;
  tier: 'individual' | 'business';
  badge?: string;
  minUsers?: number;
  maxUsers?: number;
  supportPercent?: number;
}

export interface BusinessTier {
  id: 'starter' | 'team' | 'fleet' | 'enterprise';
  name: string;
  pricePerUser: number;
  minUsers: number;
  maxUsers: number | null; // null = unlimited
  supportPercent: number;
}

export const MIN_BUSINESS_LICENSES = 4;
export const MAX_BUSINESS_LICENSES = 10000;
export const BUSINESS_DOC_FEE_MONTHLY = 500;
export const BUSINESS_DOC_FEE_YEARLY = 5000;
/** Operator documentation fee USD — 2× contractor (Cipher-locked). */
export const OPERATOR_BUSINESS_DOC_FEE_MONTHLY = 1000;
export const OPERATOR_BUSINESS_DOC_FEE_YEARLY = 10000;
export const BUSINESS_DOC_FEE_SUPPORT_PERCENT = 20;
export const CUSTOM_BOT_SETUP_FEE = 1000;
export const CYBER_CLASS_6H_PRICE = 95;
export const APPLICATION_SUPPORT_OVERAGE_RATE = 75;

export interface ApplicationSupportPricing {
  tierId: 'support_5_25' | 'support_26_100' | 'support_101_250' | 'support_251_plus';
  label: string;
  seatRangeLabel: string;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  includedHoursMonthly: number;
  overageRate: number;
  noRollover: boolean;
}

export type BusinessServicePlanId =
  | 'tech_support_hourly'
  | 'software_dev_hourly'
  | 'drone_photos'
  | 'drone_video_unedited'
  | 'ai_integration_assessment';

export const BUSINESS_TIERS: BusinessTier[] = [
  { id: 'starter', name: 'Starter', pricePerUser: 7, minUsers: 4, maxUsers: 25, supportPercent: 20 },
  { id: 'team', name: 'Team', pricePerUser: 6, minUsers: 26, maxUsers: 100, supportPercent: 20 },
  { id: 'fleet', name: 'Fleet', pricePerUser: 5, minUsers: 101, maxUsers: 250, supportPercent: 20 },
  { id: 'enterprise', name: 'Enterprise', pricePerUser: 4, minUsers: 251, maxUsers: null, supportPercent: 20 },
];

export function getBusinessTierForUsers(userCount: number): BusinessTier | null {
  if (!Number.isSafeInteger(userCount) || userCount < MIN_BUSINESS_LICENSES || userCount > MAX_BUSINESS_LICENSES) return null;
  return BUSINESS_TIERS.find(tier => 
    userCount >= tier.minUsers && (tier.maxUsers === null || userCount <= tier.maxUsers)
  ) || BUSINESS_TIERS[BUSINESS_TIERS.length - 1]; // Default to enterprise for 251+
}

export function calculateBusinessPrice(
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = 'yearly',
  pricingVersion: CompanyPlanPricingVersion = COMPANY_PLAN_PRICING_VERSION,
  lane: CompanyPlanLane = 'contractor',
): {
  pricingVersion: CompanyPlanPricingVersion;
  totalSeats: number;
  includedSeats: number;
  billableSeats: number;
  tier: BusinessTier;
  total: number;
  perUser: number;
  documentationFee: number;
  grandTotal: number;
  documentationFeeLabel: string;
  seatSupportAllocation: number;
  documentationSupportAllocation: number;
  supportAllocation: number;
  supportAllocationLabel: string;
  interval: 'month' | 'year';
  companyLane: CompanyPlanLane;
} | null {
  const tier = getBusinessTierForUsers(userCount);
  const policy = calculateCompanyPlanPrice(userCount, billingCycle, pricingVersion, lane);
  if (!tier || !policy) return null;

  const perUser = billingCycle === 'monthly'
    ? tier.pricePerUser
    : tier.pricePerUser * 10; // 2 months free on annual
  const documentationFee = policy.baseFeeCents / 100;
  const seatSubtotal = policy.seatSubtotalCents / 100;
  const seatSupportPercent = WHI_HOBBS_COMMERCIAL_SEAT_PERCENT;
  const seatSupportAllocation = seatSubtotal * (seatSupportPercent / 100);
  const documentationSupportAllocation = documentationFee * (BUSINESS_DOC_FEE_SUPPORT_PERCENT / 100);
  const supportAllocation = seatSupportAllocation + documentationSupportAllocation;

  return {
    pricingVersion,
    totalSeats: userCount,
    includedSeats: policy.includedSeats,
    billableSeats: policy.billableSeats,
    tier,
    perUser,
    total: seatSubtotal,
    documentationFee,
    grandTotal: seatSubtotal + documentationFee,
    documentationFeeLabel: policy.documentationFeeLabel,
    seatSupportAllocation,
    documentationSupportAllocation,
    supportAllocation,
    supportAllocationLabel: '20% of seats + 20% of documentation fee',
    interval: billingCycle === 'monthly' ? 'month' : 'year',
    companyLane: lane,
  };
}

export const INDIVIDUAL_DAY_PASS_PRICE = 4.99;
export const INDIVIDUAL_MONTHLY_PRICE = 14.99;
export const INDIVIDUAL_YEARLY_PRICE = 135;
/** Share of an Individual subscription that goes to WHI Hobbs. Matches `resolveDonationPercent`. */
export const WHI_HOBBS_INDIVIDUAL_PERCENT = 20;
/** Share of Commercial seat revenue that goes to WHI Hobbs. Matches `resolveDonationPercent`. */
export const WHI_HOBBS_COMMERCIAL_SEAT_PERCENT = 20;
/** Share of the business documentation fee that goes to WHI Hobbs. */
export const WHI_HOBBS_DOC_FEE_PERCENT = BUSINESS_DOC_FEE_SUPPORT_PERCENT;

export function roundUsdCents(amount: number): number {
  return Math.round(Number(amount || 0) * 100) / 100;
}

export function formatUsdAmount(amount: number): string {
  return roundUsdCents(amount).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export function calculateIndividualWhiHobbsDonation(
  billingCycle: 'dayPass' | 'monthly' | 'yearly',
  catalogPrice?: number,
): { amount: number; percent: number; interval: 'day' | 'month' | 'year'; formatted: string } {
  const price = typeof catalogPrice === 'number' && Number.isFinite(catalogPrice) && catalogPrice > 0
    ? catalogPrice
    : billingCycle === 'dayPass'
      ? INDIVIDUAL_DAY_PASS_PRICE
      : billingCycle === 'yearly'
        ? INDIVIDUAL_YEARLY_PRICE
        : INDIVIDUAL_MONTHLY_PRICE;
  const amount = roundUsdCents(price * (WHI_HOBBS_INDIVIDUAL_PERCENT / 100));
  return {
    amount,
    percent: WHI_HOBBS_INDIVIDUAL_PERCENT,
    interval: billingCycle === 'dayPass' ? 'day' : billingCycle === 'yearly' ? 'year' : 'month',
    formatted: formatUsdAmount(amount),
  };
}

export function calculateBusinessWhiHobbsDonation(
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = 'yearly',
): {
  amount: number;
  seatAmount: number;
  documentationAmount: number;
  percent: number;
  interval: 'month' | 'year';
  formatted: string;
} | null {
  const pricing = calculateBusinessPrice(userCount, billingCycle);
  if (!pricing) return null;
  const seatAmount = roundUsdCents(pricing.total * (WHI_HOBBS_COMMERCIAL_SEAT_PERCENT / 100));
  const documentationAmount = roundUsdCents(pricing.documentationFee * (WHI_HOBBS_DOC_FEE_PERCENT / 100));
  const amount = roundUsdCents(seatAmount + documentationAmount);
  return {
    amount,
    seatAmount,
    documentationAmount,
    percent: WHI_HOBBS_COMMERCIAL_SEAT_PERCENT,
    interval: pricing.interval,
    formatted: formatUsdAmount(amount),
  };
}

export function calculateApplicationSupportPrice(
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = 'yearly'
): ApplicationSupportPricing | null {
  if (userCount < MIN_BUSINESS_LICENSES || userCount > MAX_BUSINESS_LICENSES) return null;
  const tiers: Array<Omit<ApplicationSupportPricing, 'billingCycle' | 'amount' | 'overageRate' | 'noRollover'> & { monthly: number; yearly: number; min: number; max: number | null }> = [
    { tierId: 'support_5_25', label: 'Application Support', seatRangeLabel: '4-25 seats', min: 4, max: 25, monthly: 99, yearly: 990, includedHoursMonthly: 1 },
    { tierId: 'support_26_100', label: 'Application Support', seatRangeLabel: '26-100 seats', min: 26, max: 100, monthly: 199, yearly: 1990, includedHoursMonthly: 2 },
    { tierId: 'support_101_250', label: 'Application Support', seatRangeLabel: '101-250 seats', min: 101, max: 250, monthly: 399, yearly: 3990, includedHoursMonthly: 4 },
    { tierId: 'support_251_plus', label: 'Application Support', seatRangeLabel: '251+ seats', min: 251, max: null, monthly: 799, yearly: 7990, includedHoursMonthly: 8 },
  ];
  const tier = tiers.find((item) => userCount >= item.min && (item.max === null || userCount <= item.max));
  if (!tier) return null;
  return {
    tierId: tier.tierId,
    label: tier.label,
    seatRangeLabel: tier.seatRangeLabel,
    billingCycle,
    amount: billingCycle === 'yearly' ? tier.yearly : tier.monthly,
    includedHoursMonthly: tier.includedHoursMonthly,
    overageRate: APPLICATION_SUPPORT_OVERAGE_RATE,
    noRollover: true,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getHostPlatform(): string {
  try {
    return String(hostWebApp?.webApp?.platform || '');
  } catch {
    return '';
  }
}

function parseHostUserFromInitData(initData: string): any | null {
  if (!initData) return null;
  try {
    const userParam = new URLSearchParams(initData).get('user');
    if (!userParam) return null;
    const parsed = JSON.parse(userParam);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

function isNativeIosAllowedCachedStatus(status: SubscriptionStatus): boolean {
  if (!(status.isSubscribed || status.status === 'trialing')) return false;
  const source = String(status.accessSource || '');
  // StoreKit personal purchase, company-sponsored access, or allowlisted tester email unlock.
  return source === 'apple_storekit'
    || source === 'business_seat'
    || source === 'stars_business_seat'
    || source === 'company_employee'
    /*
     * company_owner was missing here while it is present in the other three
     * allowlists, so an owner launching with no signal was refused the grace
     * cache and dropped straight to the paywall.
     */
    || source === 'company_owner'
    || source === 'hauler_free'
    || source === 'test_allowlist'
    || source === 'internal_entitlement';
}

class SubscriptionService {
  private cachedStatus: SubscriptionStatus | null = null;
  private cacheTimestamp: number = 0;
  private cachedAccount = '';
  private cacheDuration = 60000; // 1 minute cache

  private rememberGraceStatus(status: SubscriptionStatus) {
    if (status.accessSource === 'telegram_trial') { this.clearGraceStatus(); return; }
    if (!status.isSubscribed && status.status !== 'trialing') return;
    if (isIosNativeAppRuntime() && !isNativeIosAllowedCachedStatus(status)) return;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(accountStorageKey(SUBSCRIPTION_GRACE_STORAGE_KEY), JSON.stringify({
        status,
        savedAt: Date.now(),
      }));
    } catch {
      // Local cache is best effort only.
    }
  }

  getCachedGraceStatus(maxAgeMs = SUBSCRIPTION_GRACE_TTL_MS): SubscriptionStatus | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(accountStorageKey(SUBSCRIPTION_GRACE_STORAGE_KEY));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { status?: SubscriptionStatus; savedAt?: number };
      if (!parsed.status || typeof parsed.savedAt !== 'number') return null;
      if (parsed.status.accessSource === 'telegram_trial') return null;
      if (Date.now() - parsed.savedAt > maxAgeMs) return null;
      if (!parsed.status.isSubscribed && parsed.status.status !== 'trialing') return null;
      if (isIosNativeAppRuntime() && !isNativeIosAllowedCachedStatus(parsed.status)) return null;
      return { ...parsed.status, source: 'cache' };
    } catch {
      return null;
    }
  }

  private clearGraceStatus() {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(accountStorageKey(SUBSCRIPTION_GRACE_STORAGE_KEY));
    } catch {
      // ignore
    }
  }

  private getUserHeaders() {
    const user = hostWebApp.getUser();
    const webSession = getWebSessionToken();
    return {
      ...(webSession ? { [WEB_SESSION_HEADER]: webSession } : {}),
      [LEGACY_AUTH_USER_ID_HEADER]: hostWebApp.getUserId() || '',
      [LEGACY_AUTH_USERNAME_HEADER]: user?.username || user?.first_name || '',
      [LEGACY_AUTH_INIT_DATA_HEADER]: hostWebApp.getInitData()
    };
  }

  private getStatusUserHeaders() {
    const initData = hostWebApp.getInitData();
    const initUser = parseHostUserFromInitData(initData);
    const liveUser = hostWebApp.getUser();
    const hasLiveInitData = initData.length > 0;

    // For live embedded app checks, prefer initData-derived IDs so a stale
    // localStorage fallback cannot accidentally verify the wrong Android user.
    const userId = initUser?.id != null
      ? String(initUser.id)
      : liveUser?.id != null
        ? String(liveUser.id)
        : hasLiveInitData
          ? ''
          : hostWebApp.getUserId() || '';

    const username = initUser?.username || liveUser?.username || initUser?.first_name || liveUser?.first_name || '';

    return {
      [LEGACY_AUTH_USER_ID_HEADER]: userId,
      [LEGACY_AUTH_USERNAME_HEADER]: username,
      [LEGACY_AUTH_INIT_DATA_HEADER]: initData,
    };
  }

  private async getStatusHeadersWithAndroidWait() {
    let headers = this.getStatusUserHeaders();
    const platform = getHostPlatform().toLowerCase();
    const shouldWait = platform.startsWith('android')
      && !headers[LEGACY_AUTH_INIT_DATA_HEADER];

    if (!shouldWait) return headers;

    for (const delay of [250, 500, 750]) {
      await sleep(delay);
      headers = this.getStatusUserHeaders();
      if (headers[LEGACY_AUTH_INIT_DATA_HEADER]) break;
    }

    return headers;
  }

  private buildSubscriptionDiagnostics(headers: Record<string, string>, source: SubscriptionStatus['source'], httpStatus?: number, contentType?: string) {
    return {
      platform: getHostPlatform() || 'unknown',
      userIdPresent: Boolean(headers[LEGACY_AUTH_USER_ID_HEADER]),
      initDataPresent: Boolean(headers[LEGACY_AUTH_INIT_DATA_HEADER]),
      source,
      httpStatus,
      contentType,
    };
  }

  /**
   * Get current subscription status
   */
  async getStatus(forceRefresh = false, signal?: AbortSignal): Promise<SubscriptionStatus> {
    let requestAccount = accountStorageKey('subscription');
    const assertCurrentAccount = () => {
      if (requestAccount !== accountStorageKey('subscription')) throw new Error('Account changed during subscription check.');
    };
    // Return cached status if valid
    if (!forceRefresh && this.cachedAccount === requestAccount && this.cachedStatus && Date.now() - this.cacheTimestamp < this.cacheDuration) {
      if ((this.cachedStatus.accessSource !== 'device_trial' || deviceTrialRemainingMs() > 0)
        && (this.cachedStatus.accessSource !== 'telegram_trial' || telegramTrialRemainingMs() > 0)) {
        return { ...this.cachedStatus, source: 'cache' };
      }
    }

    // Native iOS: StoreKit first, then company-sponsored access via /api/subscription/status
    // (employee join code / business seat). Personal non-Apple payments never unlock here.
    if (isIosNativeAppRuntime()) {
      const checkedAt = new Date().toISOString();
      try {
        const entitlements = await iosStoreKitService.getEntitlements();
        const activeTransaction = entitlements.transactions
          .filter((transaction) => Boolean(planForIosProductId(transaction.productId)))
          .sort((a, b) => String(b.expiresDate || b.purchaseDate).localeCompare(String(a.expiresDate || a.purchaseDate)))[0];
        const productId = entitlements.productId || activeTransaction?.productId;
        const plan = planForIosProductId(productId);
        const storeKitActive = entitlements.active === true && Boolean(plan);
        if (storeKitActive) {
          clearDeviceTrialLease();
          assertCurrentAccount();
          await connectApplePurchase(entitlements);
          // The authenticated Apple exchange may replace an anonymous install
          // with its stable personal account; it rejects unrelated switches.
          requestAccount = accountStorageKey('subscription');
          const status: SubscriptionStatus = {
            isSubscribed: true,
            status: 'active',
            type: plan === 'dayPass' ? 'dayPass' : plan === 'yearly' ? 'yearly' : plan === 'monthly' ? 'monthly' : undefined,
            rawPlan: productId,
            expiresAt: entitlements.expiresDate || activeTransaction?.expiresDate,
            isTrialing: false,
            trialDaysRemaining: 0,
            canTrial: false,
            checkedAt,
            source: 'api',
            accessSource: 'apple_storekit',
            paymentRequired: false,
          };
          assertCurrentAccount();
          this.cachedAccount = requestAccount;
          this.cachedStatus = status;
          this.cacheTimestamp = Date.now();
          this.rememberGraceStatus(status);
          return status;
        }
      } catch (error) {
        // Fall through to company-sponsored server check.
        console.warn('[Subscription] StoreKit entitlement check failed, trying company access:', error);
      }

      let trialUsed = false;
      let trialAvailable = false;
      try {
        const trial = await getDeviceTrialStatus(false, signal);
        trialUsed = trial.used;
        trialAvailable = trial.canTrial;
        const linked = (await import('./nativeApiRouting')).getNativeIosLinkedUser();
        if (trial.active && (!linked || linked.userId.startsWith('zwn_trial_'))) {
          requestAccount = accountStorageKey('subscription');
          const status: SubscriptionStatus = {
            isSubscribed: true, status: 'trialing', rawPlan: 'individual',
            expiresAt: trial.expiresAt || undefined, isTrialing: true,
            trialDaysRemaining: Math.max(0, Math.ceil((Date.parse(trial.expiresAt!) - Date.parse(trial.serverNow)) / 86400000)),
            canTrial: false, checkedAt, source: 'api', accessSource: 'device_trial', paymentRequired: false,
          };
          this.cachedAccount = requestAccount;
          this.cachedStatus = status;
          this.cacheTimestamp = Date.now();
          this.clearGraceStatus();
          return status;
        }
      } catch {
        // Never grant trial access from an offline/local grace cache.
      }

      try {
        // Include native session after owner/employee link so server can resolve company_owner access.
        const { getNativeIosAuthHeaders } = await import('./nativeApiRouting');
        const headers = {
          ...(await this.getStatusHeadersWithAndroidWait()),
          ...(getNativeIosAuthHeaders() || {}),
        };
        const response = await fetch(`${API_BASE}/status`, { headers, signal });
        const data = await response.json().catch(() => ({}));
        assertCurrentAccount();
        if (response.ok && data?.companyPlanRequired === true) {
          const status: SubscriptionStatus = {
            isSubscribed: false, status: 'none', rawPlan: 'company_plan_required',
            isTrialing: false, trialDaysRemaining: 0, canTrial: false,
            checkedAt, source: 'api', accessSource: 'company_plan_required',
            paymentRequired: true, companyPlanRequired: true,
            hint: data.hint || data.message,
          };
          this.cachedAccount = requestAccount;
          this.cachedStatus = status;
          this.cacheTimestamp = Date.now();
          this.clearGraceStatus();
          return status;
        }
        if (response.ok && data?.active) {
          const source = String(data.accessSource || '');
          /*
           * Renamed from companySponsored. A hauler grant is not company
           * sponsored, and leaving the old name is how somebody later adds a
           * company-only side effect inside this block and breaks haulers.
           */
          const serverGrantedAccess = source === 'business_seat'
            || source === 'stars_business_seat'
            || source === 'company_employee'
            || source === 'company_owner'
            || source === 'hauler_free'
            || source === 'wm_rental_customer'
            || source === 'test_allowlist'
            || source === 'internal_entitlement';
          if (serverGrantedAccess) {
            clearDeviceTrialLease();
            const resolvedCompanyName = String(
              data.companyName
              || data.companyEmployee?.companyName
              || data.companyOwner?.companyName
              || '',
            ).trim() || undefined;
            // Keep local linked-user display name in sync when server knows it.
            if (resolvedCompanyName) {
              try {
                const { getNativeIosLinkedUser, setNativeIosLinkedUser } = await import('./nativeApiRouting');
                const linked = getNativeIosLinkedUser();
                if (linked?.userId && !linked.companyName) {
                  setNativeIosLinkedUser({ ...linked, companyName: resolvedCompanyName });
                }
              } catch {
                // Best-effort only — display can still fall back to "your company".
              }
            }
            const status: SubscriptionStatus = {
              isSubscribed: true,
              status: 'active',
              type: source === 'company_owner'
                ? 'company_owner'
                : source === 'company_employee'
                  ? 'company_employee'
                  : source.includes('business')
                    ? 'business_starter'
                    : undefined,
              rawPlan: data.plan || source,
              expiresAt: data.expiresAt || undefined,
              isTrialing: false,
              trialDaysRemaining: 0,
              canTrial: false,
              checkedAt,
              source: 'api',
              accessSource: source,
              paymentRequired: false,
              businessSeat: data.businessSeat || null,
              isBusinessPlus: false,
              companyName: resolvedCompanyName,
              companyRole: data.companyEmployee?.role || data.companyOwner?.role || undefined,
              scopedAccess: data.scopedAccess || (source === 'wm_rental_customer' ? 'waste_management' : null),
              wmRental: data.wmRental || null,
            };
            assertCurrentAccount();
            this.cachedAccount = requestAccount;
            this.cachedStatus = status;
            this.cacheTimestamp = Date.now();
            this.rememberGraceStatus(status);
            return status;
          }
        }
      } catch (error) {
        console.warn('[Subscription] Company access status check failed:', error);
      }

      const none: SubscriptionStatus = {
        isSubscribed: false,
        status: trialUsed ? 'expired' : 'none',
        isTrialing: false,
        trialDaysRemaining: 0,
        canTrial: trialAvailable,
        checkedAt,
        source: 'api',
        accessSource: 'apple_storekit',
        paymentRequired: true,
      };
      assertCurrentAccount();
      this.cachedAccount = requestAccount;
      this.cachedStatus = none;
      this.cacheTimestamp = Date.now();
      return none;
    }

    const checkedAt = new Date().toISOString();
    const headers = await this.getStatusHeadersWithAndroidWait();
    assertCurrentAccount();
    const requestStarted = performance.now();
    try {
      const response = await fetch(`${API_BASE}/status`, {
        headers,
        signal,
      });

      let data: any;
      const contentType = response.headers.get('content-type') || '';
      try {
        if (!contentType.includes('application/json')) {
          const body = await response.text().catch(() => '');
          const geoBlocked = response.status === 403 && /outside the US|VPN/i.test(body);
          const lastError = geoBlocked
            ? 'Subscription verification was blocked by the location check. Turn off VPN or use a US-based connection and retry.'
            : `Subscription service returned ${contentType || 'a non-JSON response'}.`;
          console.warn('[Subscription] Non-JSON status response:', this.buildSubscriptionDiagnostics(headers, 'parse', response.status, contentType));
          return {
            isSubscribed: false,
            status: 'error',
            isTrialing: false,
            trialDaysRemaining: 0,
            canTrial: false,
            checkedAt,
            lastError,
            hint: geoBlocked ? 'vpn-or-location-check' : 'non-json-response',
            source: 'parse',
            httpStatus: response.status,
          };
        }
        data = await response.json();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid subscription response';
        console.warn('[Subscription] Failed to parse status response:', this.buildSubscriptionDiagnostics(headers, 'parse', response.status, contentType));
        return {
          isSubscribed: false,
          status: 'error',
          isTrialing: false,
          trialDaysRemaining: 0,
          canTrial: false,
          checkedAt,
          lastError: message,
          source: 'parse',
          httpStatus: response.status,
        };
      }

      if (!response.ok) {
        console.warn('[Subscription] Status HTTP error:', this.buildSubscriptionDiagnostics(headers, 'http', response.status, contentType));
        return {
          isSubscribed: false,
          status: 'error',
          isTrialing: false,
          trialDaysRemaining: 0,
          canTrial: false,
          checkedAt,
          lastError: data?.error || data?.reason || `Subscription check failed (${response.status})`,
          hint: data?.hint,
          source: 'http',
          httpStatus: response.status,
        };
      }

      const rawPlan = typeof data.plan === 'string'
        ? data.plan
        : typeof data.type === 'string'
          ? data.type
          : undefined;
      const normalizedType = rawPlan?.includes('business')
        ? 'business_starter'
        : rawPlan?.includes('year')
          ? 'yearly'
          : rawPlan?.includes('month') || rawPlan === 'individual'
            ? 'monthly'
            : undefined;
      const rawStatus: SubscriptionStatus['status'] = typeof data.status === 'string'
        ? (data.status as SubscriptionStatus['status'])
        : data.active
          ? 'active'
          : data.trialing
            ? 'trialing'
            : (data.expiresAt || data.trialEndsAt)
              ? 'expired'
              : 'none';

      assertCurrentAccount();
      updateTelegramTrialLease(data, requestStarted, requestAccount);
      const isSubscribed = Boolean(data.active || data.trialing)
        && (data.accessSource !== 'telegram_trial' || telegramTrialRemainingMs() > 0);
      const status: SubscriptionStatus = {
        isSubscribed,
        status: data.accessSource === 'telegram_trial' && !isSubscribed ? 'expired' : rawStatus,
        type: normalizedType,
        rawPlan,
        expiresAt: data.expiresAt,
        isTrialing: Boolean(data.trialing) && isSubscribed,
        trialDaysRemaining: data.trialEndsAt ? Math.max(0, Math.ceil((new Date(data.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0,
        canTrial: !isSubscribed && data.accessSource !== 'telegram_trial' && data.companyPlanRequired !== true,
        checkedAt,
        source: 'api',
        isBusinessPlus: false,
        accessSource: data.accessSource,
        paymentRequired: data.paymentRequired === true,
        companyPlanRequired: data.companyPlanRequired === true,
        hint: data.hint || data.message,
        businessSeat: data.businessSeat || null,
        scopedAccess: data.scopedAccess
          || (String(data.accessSource || '') === 'wm_rental_customer' ? 'waste_management' : null),
        wmRental: data.wmRental || null,
      };
      
      assertCurrentAccount();
      this.cachedAccount = requestAccount;
      this.cachedStatus = status;
      this.cacheTimestamp = Date.now();
      if (status.isSubscribed || status.status === 'trialing') {
        this.rememberGraceStatus(status);
      } else {
        this.clearGraceStatus();
      }
      
      return status;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        isSubscribed: false,
        status: 'error',
        isTrialing: false,
        trialDaysRemaining: 0,
        canTrial: false,
        checkedAt,
        lastError: error instanceof Error ? error.message : 'Failed to verify subscription',
        source: 'network',
      };
    }
  }

  /**
   * Check if user is subscribed (active or trialing)
   */
  async isSubscribed(): Promise<boolean> {
    const status = await this.getStatus();
    return status.isSubscribed;
  }

  /**
   * Validate a promo code
   */
  async validatePromo(code: string): Promise<PromoValidation> {
    try {
      const response = await fetch(`${API_BASE}/promo?code=${encodeURIComponent(code)}`, {
        headers: this.getUserHeaders()
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to validate promo:', error);
      return { valid: false, error: 'Failed to validate promo code' };
    }
  }

  /**
   * Apply a promo code to user's account
   */
  async applyPromo(code: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE}/promo`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to apply promo:', error);
      return { success: false, error: 'Failed to apply promo code' };
    }
  }

  /**
   * Web (browser) Stripe checkout for INDIVIDUAL plans. The native iOS app
   * never calls this — it sells individual access through Apple StoreKit.
   */
  async initiateIndividualWebCheckout(
    billingCycle: 'dayPass' | 'monthly' | 'yearly',
  ): Promise<{ checkoutUrl?: string; error?: string; code?: string; notLiveYet?: boolean }> {
    try {
      const planId = billingCycle === 'dayPass'
        ? 'individual_day_pass'
        : billingCycle === 'yearly'
          ? 'individual_yearly'
          : 'individual_monthly';
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId,
          billingCycle: billingCycle === 'dayPass' ? 'day' : billingCycle,
          successUrl: `${window.location.origin}/?checkout=success`,
          cancelUrl: `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return data;
      }
      if (data?.error) {
        return {
          error: data.notLiveYet
            ? `${data.error}${data.hint ? ` ${data.hint}` : ''}`
            : String(data.error),
          code: data.code,
          notLiveYet: data.notLiveYet === true,
        };
      }
      return data;
    } catch (error) {
      console.error('Individual web checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Legacy bot-based checkout entry (kept for old callers): routes to the
   * web Stripe checkout.
   */
  async initiateCheckout(plan: PlanId): Promise<void> {
    const key = String(plan);
    await this.initiateIndividualWebCheckout(
      key.includes('day') ? 'dayPass' : key.includes('year') ? 'yearly' : 'monthly',
    );
  }

  /**
   * Get pricing plans
   */
  getPricingPlans(): PricingPlan[] {
    return [
      {
        id: 'individual_day_pass',
        name: 'Individual 1-Day Access',
        price: INDIVIDUAL_DAY_PASS_PRICE,
        interval: 'day',
        trialDays: 0,
        tier: 'individual',
        features: [
          '24-hour access to ZiaWellNav Individual tools',
          'One-time payment · does not auto-renew',
          'Upgrade to Monthly or Annual anytime',
        ]
      },
      {
        id: 'individual_monthly',
        name: 'Individual Monthly',
        price: 14.99,
        interval: 'month',
        trialDays: 7,
        tier: 'individual',
        features: [
          '🗺️ 1.4 Million Wells & Facilities (NM + TX)',
          '🔊 Voice Navigation & Turn-by-Turn Directions',
          '📋 Pre/Post Trip Vehicle Inspections',
          '🦺 Job Safety Analysis (JSA) with Crew Sign-Off',
          '👥 Crew Management & Marching Orders',
          '⏱️ Work Timer & Timesheets',
          '🤖 AI Assistant (Grok)',
          '⚠️ Road Hazard Reporting & Alerts',
          '🌤️ Weather, Air Quality & Pollen Data',
          '🔐 Cert & License Vault',
          '📸 Expense Receipts & Photo Ticket OCR',
          '🌮 Community Routes, Poop Pins & Taco Trucks',
          '📱 Offline Maps & Background Sync (PWA)',
          '🌐 English & Spanish',
          '✅ Native iOS access with Apple billing'
        ]
      },
      {
        id: 'individual_yearly',
        name: 'Individual Annual',
        // Match landing: $14.99/mo · $135/yr = 9×$14.99 (3 months free vs $179.88)
        price: 135,
        originalPrice: 179.88, // $14.99 × 12
        interval: 'year',
        trialDays: 7,
        popular: true,
        savings: '3 months free · save ~$45 vs monthly',
        tier: 'individual',
        features: [
          '🗺️ 1.4 Million Wells & Facilities (NM + TX)',
          '🔊 Voice Navigation & Turn-by-Turn Directions',
          '📋 Pre/Post Trip Vehicle Inspections',
          '🦺 Job Safety Analysis (JSA) with Crew Sign-Off',
          '👥 Crew Management & Marching Orders',
          '⏱️ Work Timer & Timesheets',
          '🤖 AI Assistant (Grok)',
          '⚠️ Road Hazard Reporting & Alerts',
          '🌤️ Weather, Air Quality & Pollen Data',
          '🔐 Cert & License Vault',
          '📸 Expense Receipts & Photo Ticket OCR',
          '🌮 Community Routes, Poop Pins & Taco Trucks',
          '📱 Offline Maps & Background Sync (PWA)',
          '🌐 English & Spanish',
          '🎁 3 months free when you pay yearly ($135/yr vs $179.88 monthly)',
          '✅ One year of native iOS access'
        ]
      }
    ];
  }

  /**
   * Get Business pricing display info
   */
  getBusinessPricingInfo() {
    return {
      tiers: BUSINESS_TIERS,
      supportPercent: 20,
      supportName: 'ZiaWellNav operations',
      features: [
        '🏢 Everything in Field Professional',
        '🖥️ Enterprise Portal & Fleet Management',
        '✅ Company Verification Badge',
        '📱 Bulk License Management',
        '💼 Jobs Board Posting (hire workers)',
        '🎯 Dedicated Support Channel',
        '💰 Volume Discounts (100+ users)',
        '✅ Admin-managed company seats'
      ]
    };
  }

  /**
   * Company seat starter plan (legacy helper name — not the old $200 vendor club).
   */
  getBusinessPlusPlan(): PricingPlan {
    return {
      id: 'business_starter',
      name: 'Business Starter',
      price: 7.00,
      interval: 'month',
      trialDays: 7,
      tier: 'business',
      badge: 'TEAM',
      minUsers: 5,
      maxUsers: 25,
      supportPercent: 20,
      features: [
        '🏢 Everything in Individual',
        '👥 5-25 user licenses',
        '💼 Company verification badge',
        '🎯 Priority enterprise support',
        '✅ Admin-managed company seats'
      ]
    };
  }

  /**
   * Web company seats use Stripe checkout; native iOS purchase restrictions are enforced separately.
   */
  async initiateBusinessCheckout(
    userCount: number,
    billingCycle: 'monthly' | 'yearly' = 'yearly',
    successUrl?: string,
    cancelUrl?: string,
    customBotSetup?: {
      includeCustomBotSetup?: boolean;
      includeApplicationSupport?: boolean;
      workflowRequest?: WorkflowRequest;
      botName?: string;
      contactEmail?: string;
      useCase?: string;
      serviceSelections?: CompanyServiceSelections;
      companyPlanLane?: 'contractor' | 'operator';
      companyName?: string;
      phone?: string;
    }
  ): Promise<{ checkoutUrl?: string; error?: string; code?: string; notLiveYet?: boolean }> {
    const pricing = calculateBusinessPrice(
      userCount,
      billingCycle,
      COMPANY_PLAN_PRICING_VERSION,
      customBotSetup?.companyPlanLane === 'operator' ? 'operator' : 'contractor',
    );
    if (!pricing) {
      return { error: `Minimum ${MIN_BUSINESS_LICENSES} users required for business plans` };
    }
    if (userCount > MAX_BUSINESS_LICENSES) {
      return { error: `Maximum ${MAX_BUSINESS_LICENSES.toLocaleString()} users supported in self-serve checkout` };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: `business_${pricing.tier.id}`,
          userCount,
          billingCycle,
          includeCustomBotSetup: customBotSetup?.includeCustomBotSetup === true,
          includeApplicationSupport: customBotSetup?.includeApplicationSupport === true,
          ...(customBotSetup?.workflowRequest ? {workflowRequest:customBotSetup.workflowRequest} : {}),
          botName: customBotSetup?.botName || '',
          contactEmail: customBotSetup?.contactEmail || '',
          useCase: customBotSetup?.useCase || '',
          ...(customBotSetup?.companyPlanLane ? { companyPlanLane: customBotSetup.companyPlanLane, audience: customBotSetup.companyPlanLane } : {}),
          ...(customBotSetup?.companyName ? { companyName: customBotSetup.companyName } : {}),
          ...(customBotSetup?.phone ? { phone: customBotSetup.phone } : {}),
          ...(customBotSetup?.serviceSelections ? { serviceSelections: customBotSetup.serviceSelections } : {}),
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return data;
      }
      if (data?.error) {
        return {
          error: data.notLiveYet
            ? `${data.error}${data.hint ? ` ${data.hint}` : ''}`
            : String(data.error),
          code: data.code,
          notLiveYet: data.notLiveYet === true,
        };
      }
      return data;
    } catch (error) {
      console.error('Business checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Web custom workflow setup uses the existing Stripe checkout flow.
   */
  async initiateCustomBotCheckout(
    metadata: { orgName: string; botName: string; contactEmail: string; useCase: string },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: 'custom_bot_setup',
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
          ...metadata,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Custom bot checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Cyber class seats — Stripe one-time `cyber_class_6h` ($95/head).
   */
  async initiateCyberClassCheckout(
    metadata: {
      attendeeCount: number;
      contactName: string;
      contactEmail: string;
      companyName?: string;
      notes?: string;
    },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    const attendeeCount = Math.trunc(Number(metadata.attendeeCount || 0));
    if (!Number.isFinite(attendeeCount) || attendeeCount < 1 || attendeeCount > 100) {
      return { error: 'Cyber class attendee count must be between 1 and 100' };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: 'cyber_class_6h',
          attendeeCount,
          contactName: metadata.contactName || '',
          contactEmail: metadata.contactEmail || '',
          companyName: metadata.companyName || '',
          notes: metadata.notes || '',
          successUrl: successUrl || `${window.location.origin}/?checkout=success&kind=service`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Cyber class checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  async initiateBusinessServiceCheckout(
    metadata: {
      planId: BusinessServicePlanId;
      quantity: number;
      contactName: string;
      contactEmail: string;
      companyName?: string;
      phone?: string;
      notes?: string;
    },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    const quantity = Math.trunc(Number(metadata.quantity || 0));
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > 100) {
      return { error: 'Business service quantity must be between 1 and 100' };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: metadata.planId,
          quantity,
          contactName: metadata.contactName || '',
          contactEmail: metadata.contactEmail || '',
          companyName: metadata.companyName || '',
          phone: metadata.phone || '',
          notes: metadata.notes || '',
          successUrl: successUrl || `${window.location.origin}/?checkout=success&kind=service`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Business service checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Retired $200 vendor-club plan. Always false — bids use approved operators.
   */
  async isBusinessPlus(): Promise<boolean> {
    return false;
  }

  /**
   * Get subscription benefits for display
   */
  getSubscriptionBenefits(): { icon: string; title: string; description: string }[] {
    return [
      {
        icon: '🗺️',
        title: 'Full Navigation',
        description: 'Navigate to any well, SWD, rig, or facility in New Mexico'
      },
      {
        icon: '📱',
        title: 'Offline Access',
        description: 'Download entire basins for offline use in remote areas'
      },
      {
        icon: '🔊',
        title: 'Voice Commands',
        description: 'Hands-free voice search and turn-by-turn directions'
      },
      {
        icon: '📢',
        title: 'Basin Channels',
        description: 'Access private channels for road conditions & safety alerts'
      },
      {
        icon: '🛤️',
        title: 'Caliche Routes',
        description: 'Community-shared routes to avoid bad roads'
      },
      {
        icon: '📸',
        title: 'Site Photos',
        description: 'View and share photos to identify locations visually'
      }
    ];
  }

  /**
   * Clear cached status (call after subscription changes)
   */
  clearCache(clearGrace = false): void {
    this.cachedStatus = null;
    this.cacheTimestamp = 0;
    if (clearGrace) this.clearGraceStatus();
  }

  /**
   * Company owner on iPhone: email a 6-digit code to verified Company.email.
   * This is separate from employee join codes and from Apple purchase allowlist.
   */
  async requestCompanyOwnerLinkCode(email: string): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      const res = await fetch('/api/auth/native-ios/company-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request_code', email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        return { success: false, error: data.error || data.message || 'Could not send company owner link code' };
      }
      return {
        success: true,
        message: data.message || 'If that verified company email exists, a link code was sent.',
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not send company owner link code' };
    }
  }

  async verifyCompanyOwnerLinkCode(email: string, code: string): Promise<{
    success: boolean;
    error?: string;
    message?: string;
    linkedUser?: {
      userId: string;
      username?: string | null;
      companyId?: string | null;
      companyName?: string | null;
      sessionToken?: string | null;
      linkedAt?: string;
    } | null;
  }> {
    try {
      const res = await fetch('/api/auth/native-ios/company-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify_code',
          email: email.trim().toLowerCase(),
          code: String(code || '').replace(/\D/g, '').slice(0, 6),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        return { success: false, error: data.error || data.message || 'Invalid or expired company link code' };
      }
      if (data.linkedUser?.userId && data.linkedUser?.sessionToken) {
        setNativeIosLinkedUser({
          userId: String(data.linkedUser.userId),
          username: data.linkedUser.username ? String(data.linkedUser.username) : null,
          companyId: data.linkedUser.companyId ? String(data.linkedUser.companyId) : null,
          companyName: data.linkedUser.companyName ? String(data.linkedUser.companyName) : null,
          sessionToken: String(data.linkedUser.sessionToken),
          linkedAt: data.linkedUser.linkedAt ? String(data.linkedUser.linkedAt) : new Date().toISOString(),
        });
      }
      this.clearCache();
      const name = data.linkedUser?.companyName || 'your company';
      return {
        success: true,
        message: `This iPhone is now the owner session for ${name}.`,
        linkedUser: data.linkedUser || null,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Could not verify company owner link code' };
    }
  }

  /**
   * Link an existing admin-managed purchase to the logged-in user by email.
   */
  async requestPurchaseRecovery(email: string): Promise<{ challenge: string; expiresAt: string; retryAfter: number }> {
    const response = await fetch('/api/subscription/link', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...this.getUserHeaders() },
      body: JSON.stringify({ action: 'send', email: email.trim().toLowerCase() }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.sent || !result?.challenge) throw new Error(result?.error || 'We could not send your code. Please try again.');
    return { challenge: result.challenge, expiresAt: result.expiresAt, retryAfter: Number(result.retryAfter) || 60 };
  }

  async verifyPurchaseRecovery(challenge: string, code: string): Promise<void> {
    const response = await fetch('/api/subscription/link', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...this.getUserHeaders() },
      body: JSON.stringify({ action: 'verify', challenge, code }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.success) throw new Error(result?.error || 'We could not recover your purchase. Please try again.');
    this.clearCache();
  }

  async linkPurchaseByEmail(email: string): Promise<{
    success: boolean;
    error?: string;
    linkedUser?: {
      userId: string;
      username?: string | null;
      companyId?: string | null;
      sessionToken?: string | null;
      linkedAt?: string;
      email?: string;
    } | null;
    message?: string;
    accessSource?: string;
  }> {
    try {
      const res = await fetch('/api/subscription/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getUserHeaders(),
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { success: false, error: data.error || 'Failed to link purchase' };
      }
      // Native iOS tester unlock returns a session token to persist on-device.
      if (data.linkedUser?.userId && data.linkedUser?.sessionToken) {
        setNativeIosLinkedUser({
          userId: String(data.linkedUser.userId),
          username: data.linkedUser.username ? String(data.linkedUser.username) : null,
          companyId: data.linkedUser.companyId ? String(data.linkedUser.companyId) : null,
          companyName: data.linkedUser.companyName
            ? String(data.linkedUser.companyName)
            : (data.companyName ? String(data.companyName) : null),
          sessionToken: String(data.linkedUser.sessionToken),
          linkedAt: data.linkedUser.linkedAt ? String(data.linkedUser.linkedAt) : new Date().toISOString(),
        });
      }
      this.clearCache();
      return {
        success: true,
        linkedUser: data.linkedUser || null,
        message: data.message,
        accessSource: data.accessSource || data.source,
      };
    } catch (err) {
      console.error('Failed to link purchase:', err);
      return { success: false, error: 'Failed to link purchase' };
    }
  }

  /**
   * Fetch license keys for a purchase email (business seats).
   */
  async fetchLicensesByEmail(email: string): Promise<{ licenses?: any[]; subscriptions?: any[]; error?: string }> {
    try {
      const res = await fetch('/api/subscription/licenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { error: data.error || 'Failed to load licenses' };
      }
      return { licenses: data.licenses, subscriptions: data.subscriptions };
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
      return { error: 'Failed to load licenses' };
    }
  }

  /**
   * Claim a license key for business plans.
   */
  async claimLicense(licenseKey: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/subscription/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getUserHeaders(),
        },
        body: JSON.stringify({ licenseKey }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { success: false, error: data.error || 'Failed to claim license' };
      }
      this.clearCache();
      return { success: true };
    } catch (err) {
      console.error('Failed to claim license:', err);
      return { success: false, error: 'Failed to claim license' };
    }
  }
}

export const subscriptionService = new SubscriptionService();
